import { doc, setDoc, getDoc, collection, getDocs, query, where, orderBy, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../../sdk_initialization';

const COLLECTION = 'transactions';

/**
 * Create a transaction record after successful payment or log failed attempts
 * @param {Object} params - Transaction parameters
 * @param {string} params.paymentIntentId - Stripe Payment Intent ID
 * @param {string} params.profileId - User/profile ID
 * @param {string} params.eventId - Event ID
 * @param {string} params.layoutId - Layout ID (optional)
 * @param {number} params.amount - Amount in cents
 * @param {string} params.currency - Currency code (e.g., 'usd')
 * @param {string} params.status - Payment status (e.g., 'incomplete', 'succeeded', 'failed', 'refunded')
 * @param {string} params.failureReason - Reason for failure (for failed transactions)
 * @param {Object} params.metadata - Payment metadata (vendor, booths, etc.)
 * @param {Array} params.booths - Array of booth objects with details
 * @param {boolean} params.manual - Whether this is a manual transaction
 * @param {Array} params.failureAttempts - Array of previous failure attempts (optional, to preserve history)
 * @param {number} params.attemptCount - Number of failed attempts (optional, to preserve count)
 * @returns {Promise<string>} Transaction ID
 */
export const createTransaction = async ({
  paymentIntentId,
  profileId,
  eventId,
  layoutId = null,
  amount,
  currency = 'usd',
  status = 'succeeded',
  failureReason = null,
  metadata = {},
  booths = [],
  failureAttempts = [],
  attemptCount = 0,
}) => {
  if (!paymentIntentId) throw new Error('Missing paymentIntentId for createTransaction');
  if (!profileId) throw new Error('Missing profileId for createTransaction');
  // Allow empty eventId for failed transactions (user might not have selected event yet)
  // if (!eventId) throw new Error('Missing eventId for createTransaction');

  // Use paymentIntentId as document ID for idempotency
  const ref = doc(db, COLLECTION, paymentIntentId);

  // Check if transaction already exists (e.g., incomplete transaction from checkout)
  // Catch permission errors - if we can't read it, treat as doesn't exist (we're about to create it)
  let exists = false;
  let existingData = null;
  try {
    const existingSnap = await getDoc(ref);
    exists = existingSnap.exists();
    if (exists) {
      existingData = existingSnap.data() || null;
    }
  } catch (error) {
    // Permission denied or other error - treat as doesn't exist
    exists = false;
    existingData = null;
  }

  // Resolve eventId/layoutId from explicit params or try common metadata locations.
  let resolvedEventId = eventId || (metadata && metadata.eventId) || '';
  if (!resolvedEventId && metadata && typeof metadata === 'object') {
    // Try nested shapes: metadata.event?.id, metadata.event_id, metadata.eventID
    if (metadata.event && typeof metadata.event === 'object') {
      resolvedEventId = metadata.eventId || metadata.event.id || resolvedEventId;
    }
    resolvedEventId = resolvedEventId || metadata.eventId || metadata.eventID || resolvedEventId;
    // As a last resort, look for well-known event id keys only (avoid picking up eventName)
    if (!resolvedEventId) {
      for (const k of Object.keys(metadata)) {
        if (!k) continue;
        const lk = k.toLowerCase();
        if ((lk === 'eventid' || lk === 'event_id' || lk === 'event') && metadata[k]) {
          resolvedEventId = metadata[k];
          break;
        }
      }
    }
  }

  const resolvedLayoutId = layoutId || (metadata && metadata.layoutId) || null;

  if (!resolvedEventId) {
    // Helpful debug: warn when eventId could not be resolved so we can trace sources
    try {
      // eslint-disable-next-line no-console
      console.warn('[transactionsService] createTransaction: no eventId resolved for paymentIntentId', paymentIntentId, 'metadata:', metadata);
    } catch (e) {}
  }

  const metadataInput = (metadata && typeof metadata === 'object') ? metadata : {};

  const transactionData = {
    paymentIntentId,
    profileId,
    eventId: resolvedEventId,
    layoutId: resolvedLayoutId,
    amount,
    currency,
    status,
    metadata: {
      source: metadataInput.source || 'ICNACON Events App',
      payeeName: metadataInput.payeeName || '',
      payeeEmail: metadataInput.payeeEmail || '',
      payeePhone: metadataInput.payeePhone || '',
      vendorName: metadataInput.vendorName || '',
      vendorNumber: metadataInput.vendorNumber || '',
      eventName: metadataInput.eventName || '',
      boothNumbers: metadataInput.boothNumbers || '',
      boothCount: metadataInput.boothCount || '0',
      ...metadataInput,
    },
    booths: booths.map(booth => ({
      id: booth.id,
      number: booth.number,
      level: booth.level,
      corner: booth.corner,
      row: booth.row,
      col: booth.col,
      price: booth.price || 0,
    })),
    refundHistory: [],
    failureAttempts: failureAttempts, // Preserve failure attempts if provided
    updatedAt: serverTimestamp(),
  };

  // Only set createdAt if this is a new transaction
  if (!exists) {
    transactionData.createdAt = serverTimestamp();
  }

  // Add failure reason and attempt info if this is a failed transaction
  if (status === 'failed' && failureReason) {
    transactionData.failureReason = failureReason;
    // Only add to failureAttempts array if not already provided
    if (!failureAttempts || failureAttempts.length === 0) {
      transactionData.failureAttempts = [{
        reason: failureReason,
        attemptedAt: serverTimestamp(),
      }];
      transactionData.attemptCount = 1;
    } else {
      transactionData.attemptCount = attemptCount;
    }
  } else if (attemptCount > 0) {
    // Preserve attempt count for succeeded transactions
    transactionData.attemptCount = attemptCount;
  }

  // Use setDoc with merge for updates, or regular setDoc for new documents
  if (exists) {
    // Update existing transaction - use setDoc with merge to update all fields while preserving existing ones
    // This ensures incomplete transactions get properly updated to succeeded/failed
    // Prepare updateData by merging with existing metadata to avoid wiping nested metadata fields
    const updateData = {
      ...transactionData,
      // Don't override createdAt - it was set when the incomplete transaction was created
      createdAt: null, // This will be ignored by merge, keeping the original
    };

    // Remove createdAt from update data since we want to preserve the original
    delete updateData.createdAt;

    // Prevent clearing existing eventId/layoutId if resolved values are empty
    if (!resolvedEventId && updateData.hasOwnProperty('eventId')) {
      delete updateData.eventId;
    }
    if (!resolvedLayoutId && updateData.hasOwnProperty('layoutId')) {
      delete updateData.layoutId;
    }

    // Merge existing metadata with new metadata so we don't accidentally overwrite nested fields (like comments)
    const existingMetadata = existingData && existingData.metadata ? existingData.metadata : {};
    updateData.metadata = {
      ...existingMetadata,
      ...(transactionData.metadata || {}),
    };

    await setDoc(ref, updateData, { merge: true });
  } else {
    // Create new transaction
    await setDoc(ref, transactionData);
  }
  
  return paymentIntentId;
};

/**
 * Get a transaction by payment intent ID
 * @param {string} paymentIntentId - Payment Intent ID
 * @returns {Promise<Object|null>} Transaction data or null
 */
export const getTransaction = async (paymentIntentId) => {
  if (!paymentIntentId) throw new Error('Missing paymentIntentId for getTransaction');
  
  const ref = doc(db, COLLECTION, paymentIntentId);
  
  try {
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch (error) {
    // If permission denied or document doesn't exist, return null
    return null;
  }
};

/**
 * List transactions for a user/profile
 * @param {string} profileId - User/profile ID
 * @param {Object} options - Query options
 * @param {number} options.limitCount - Maximum number of results
 * @returns {Promise<Array>} Array of transactions
 */
export const listTransactionsForProfile = async (profileId, { limitCount = 100 } = {}) => {
  if (!profileId) return [];
  
  const col = collection(db, COLLECTION);
  const q = query(
    col,
    where('profileId', '==', profileId),
    orderBy('createdAt', 'desc')
  );
  
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

/**
 * List transactions for an event
 * @param {string} eventId - Event ID
 * @param {Object} options - Query options
 * @param {number} options.limitCount - Maximum number of results
 * @returns {Promise<Array>} Array of transactions
 */
export const listTransactionsForEvent = async (eventId, { limitCount = 100 } = {}) => {
  if (!eventId) return [];
  
  const col = collection(db, COLLECTION);
  const q = query(
    col,
    where('eventId', '==', eventId),
    orderBy('createdAt', 'desc')
  );
  
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

/**
 * Record a refund for a transaction
 * @param {string} paymentIntentId - Payment Intent ID
 * @param {Object} refundData - Refund details
 * @param {string} refundData.refundId - Stripe Refund ID
 * @param {number} refundData.amount - Refund amount in cents
 * @param {string} refundData.reason - Reason for refund
 * @param {string} refundData.status - Refund status
 * @returns {Promise<void>}
 */
export const recordRefund = async (paymentIntentId, { refundId, amount, reason = '', status = 'succeeded', boothIds = [], boothsTotal = 0, refundFee = 0, additionalRefund = 0 }) => {
  if (!paymentIntentId) throw new Error('Missing paymentIntentId for recordRefund');
  if (!refundId) throw new Error('Missing refundId for recordRefund');
  
  const ref = doc(db, COLLECTION, paymentIntentId);
  const snap = await getDoc(ref);
  
  if (!snap.exists()) {
    throw new Error('Transaction not found');
  }
  
  const transaction = snap.data();
  const refundHistory = transaction.refundHistory || [];
  
  // Add refund to history with ISO timestamp (serverTimestamp not supported in arrays)
  refundHistory.push({
    refundId,
    amount,
    reason,
    status,
    boothIds: boothIds || [], // Track which booths were refunded
    boothsTotal: boothsTotal || 0, // Total amount for booths before fees
    refundFee: refundFee || 0, // Refund fee deducted
    additionalRefund: additionalRefund || 0, // Additional refund amount
    refundedAt: new Date().toISOString()
  });
  
  // Calculate total refunded amount
  const totalRefunded = refundHistory
    .filter(r => r.status === 'succeeded')
    .reduce((sum, r) => sum + r.amount, 0);
  
  // Update transaction status if fully refunded
  const newStatus = totalRefunded >= transaction.amount ? 'refunded' : 'partially_refunded';
  
  await updateDoc(ref, {
    status: newStatus,
    refundHistory,
    totalRefunded,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Process a refund for a transaction
 * Calls Firebase Cloud Function to process the refund via Stripe API
 * @param {string} paymentIntentId - Payment Intent ID
 * @param {Object} refundData - Refund details
 * @param {number} refundData.amount - Refund amount in cents
 * @param {string} refundData.reason - Reason for refund
 * @param {Array<string>} refundData.boothIds - Array of booth IDs being refunded
 * @param {number} refundData.boothsTotal - Total booth amount before fees
 * @param {number} refundData.refundFee - Refund fee deducted
 * @param {number} refundData.additionalRefund - Additional refund amount
 * @returns {Promise<void>}
 */
export const processRefund = async (paymentIntentId, { amount, reason = 'Requested by admin', boothIds = [], boothsTotal = 0, refundFee = 0, additionalRefund = 0, eventId = '' }) => {
  if (!paymentIntentId) throw new Error('Missing paymentIntentId for processRefund');
  if (!amount || amount <= 0) throw new Error('Invalid refund amount');
  
  // Import Firebase Functions
  const { getFunctions, httpsCallable } = await import('firebase/functions');
  const functions = getFunctions();
  
  // Call the Cloud Function to process the refund via Stripe
  const processRefundFn = httpsCallable(functions, 'processRefund');
  
  try {
    const result = await processRefundFn({
      paymentIntentId,
      amount,
      reason,
      eventId,
    });
    
    if (!result.data.success) {
      throw new Error(result.data.error || 'Refund failed');
    }
    
    // Record the refund in Firestore with complete breakdown
    await recordRefund(paymentIntentId, {
      refundId: result.data.refundId,
      amount: result.data.amount,
      reason,
      status: result.data.status,
      boothIds: boothIds || [], // Pass booth IDs to record in refund history
      boothsTotal: boothsTotal || 0, // Booth total before fees
      refundFee: refundFee || 0, // Fee deducted from refund
      additionalRefund: additionalRefund || 0, // Additional refund amount
    });
    
    return result.data;
  } catch (error) {
    console.error('[processRefund] Error:', error);
    throw new Error(error?.message || 'Failed to process refund');
  }
};

/**
 * Update transaction status
 * @param {string} paymentIntentId - Payment Intent ID
 * @param {string} status - New status
 * @returns {Promise<void>}
 */
export const updateTransactionStatus = async (paymentIntentId, status) => {
  if (!paymentIntentId) throw new Error('Missing paymentIntentId for updateTransactionStatus');
  if (!status) throw new Error('Missing status for updateTransactionStatus');
  
  const ref = doc(db, COLLECTION, paymentIntentId);
  await updateDoc(ref, {
    status,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Add a failed payment attempt to the transaction record
 * @param {string} paymentIntentId - Payment Intent ID
 * @param {string} failureReason - Reason for the failure
 * @returns {Promise<void>}
 */
export const addFailureAttempt = async (paymentIntentId, failureReason) => {
  if (!paymentIntentId) throw new Error('Missing paymentIntentId for addFailureAttempt');
  if (!failureReason) throw new Error('Missing failureReason for addFailureAttempt');
  
  const ref = doc(db, COLLECTION, paymentIntentId);
  const snap = await getDoc(ref);
  
  if (!snap.exists()) {
    console.error('[addFailureAttempt] Transaction not found:', paymentIntentId);
    console.error('[addFailureAttempt] Cannot add failure attempt - incomplete transaction was not created');
    throw new Error('Transaction record not found. Please try again.');
  }
  
  const transaction = snap.data();
  const failureAttempts = transaction.failureAttempts || [];
  
  // Add new failure attempt with timestamp
  failureAttempts.push({
    reason: failureReason,
    attemptedAt: new Date().toISOString(), // Use ISO string instead of serverTimestamp for immediate value
  });
  
  // Update transaction with new attempt and failed status
  try {
    await updateDoc(ref, {
      status: 'failed',
      failureReason: failureReason, // Keep most recent failure as top-level field
      failureAttempts,
      attemptCount: failureAttempts.length,
      updatedAt: serverTimestamp(),
    });
  } catch (updateError) {
    console.error('[addFailureAttempt] ❌ Failed to update transaction:', updateError);
    throw updateError;
  }
};
